/* ==========================================================================
   LIFTSTEIN v2 - hero dial auto-cycle

   WHY THIS FILE EXISTS
   The four dial panels are clickable links, but nothing at rest says so. This
   walks the dial through its own four states so a visitor sees the panels
   light, MAX turn his head and the machine arrive - the exact sequence they
   would get by hovering - without having to discover the hover first.

   WHY IT IS NOT IN THE SECTION
   sections/liftstein-hero.liquid is locked. Everything here is driven by ONE
   attribute the section already owns: data-cat on section.ls-hero. Setting it
   to fork|reach|pallet|stack triggers the approved cross-fade, the baked MAX
   head-turn and the vector outline draw-in. No new artwork, no markup change.

   THE LOOP
   Idle rest, the four panels back to back, idle rest, again - for as long as
   the hero is on screen, on both mobile and desktop. It never ends by itself
   and it never returns to idle between panels; idle is punctuation between
   passes, not a gap between states.

   NO IDLE BETWEEN PANELS
   Both skins hand one state to the next in a way that lets the idle plate show
   through mid-switch. On mobile it is explicit - the outgoing panel fades out,
   the wheel sits dark for ~150ms, then the incoming panel fades in. On desktop
   it is milder: the outgoing plate fades faster than the incoming one arrives,
   so about 40% of the idle scene bleeds through for a moment. Repeated forever,
   both read as the dial flinching back to idle on every switch.
   While the cycle is driving (and only then) the block below retimes that
   hand-over into a true dissolve:
     - the outgoing full-frame plate HOLDS at full opacity until the incoming
       one has completely covered it, then drops behind it. The incoming plate
       sits above the outgoing one (z-index:1), so this is a clean A-to-B
       dissolve: the idle plate is never revealed and the two machines never
       ghost through each other;
     - on mobile the wedges cross directly, so the gold light travels around
       the dial instead of blinking out between panels.
   Returning to idle after the fourth panel is an ordinary fade-out, handled by
   the [data-cat=""] rule.
   Every rule is scoped to [data-ls-cycling], so a visitor's own hover, tap and
   focus still use the section's original approved timings, untouched.

   THE THREE THINGS THAT MUST NOT HAPPEN
   1. It must never load an image. The hero defers the four hover plates until
      after LCP. This file only OBSERVES readiness and refuses to start until
      the section has loaded them itself.
   2. It must never fight the visitor. Hover, tap or keyboard focus parks it
      immediately; it picks the sequence up again once they let go.
   3. It must never run where it would do nothing or do harm: between 750px and
      1199px the skin hides the hover artwork, and under reduced motion the
      transitions are 0.001ms, which would make this a strobe of hard cuts.
   ========================================================================== */

(function () {
  'use strict';

  var SEQ = ['fork', 'reach', 'pallet', 'stack']; /* left to right around the dial */

  var CFG = {
    beat: 2400,       /* ms per panel: ~0.5s of dissolve, then ~1.9s perfectly still */
    rest: 2000,       /* ms of idle between passes - long enough to read as a pause */
    lead: 1800,       /* ms of idle on load before the first pass begins */
    resume: 1600,     /* ms after an interruption before starting a fresh pass */
    revs: Infinity,   /* passes before stopping - endless unless ?lscycle says otherwise */
    cross: 520,       /* ms plate dissolve */
    wcross: 420,      /* ms wedge cross-fade (mobile) */
    hold: 560,        /* ms the outgoing plate holds - MUST exceed cross */
    minw: 1200        /* below this, and above 749, the hover artwork is display:none */
  };

  /* ---- tuning override, for review only ---------------------------------
     ?lscycle=off | slow | brisk | loop      or  ?lscycle=<beat>,<rest>,<revs>
     Values are clamped so a bad parameter can never produce a strobe. ----- */
  try {
    var q = new URLSearchParams(window.location.search).get('lscycle');
    if (q === 'off') return;
    if (q) {
      var presets = {
        slow:  { beat: 3000, rest: 2400 },
        brisk: { beat: 1900, rest: 1400 },
        loop:  { revs: Infinity },
        'default': {}
      };
      if (Object.prototype.hasOwnProperty.call(presets, q)) {
        var pr = presets[q];
        for (var k in pr) { if (Object.prototype.hasOwnProperty.call(pr, k)) CFG[k] = pr[k]; }
      } else {
        var n = q.split(',').map(Number);
        if (isFinite(n[0])) CFG.beat = Math.min(8000, Math.max(900, n[0]));
        if (isFinite(n[1])) CFG.rest = Math.min(8000, Math.max(0, n[1]));
        if (isFinite(n[2])) CFG.revs = Math.min(999, Math.max(1, n[2]));
      }
    }
  } catch (e) { /* a malformed parameter must never break the hero */ }

  if (CFG.hold <= CFG.cross) CFG.hold = CFG.cross + 40; /* the hold must outlast the dissolve */

  /* ---- the dissolve retiming, injected once -----------------------------
     Scoped to [data-ls-cycling]; inert at every other time. */
  function injectCSS() {
    if (document.getElementById('ls-cycle-crossfade')) return;
    var sel = function (tpl) {
      var out = [];
      for (var i = 0; i < SEQ.length; i++) out.push(tpl.replace(/%c/g, SEQ[i]));
      return out.join(',');
    };
    var css =
      /* ---------- desktop ---------- */
      '@media screen and (min-width:750px){' +
        'section.ls-hero[data-ls-cycling] .ls-layer{' +
          'transition:opacity 0s linear ' + CFG.hold + 'ms}' +
        sel('section.ls-hero[data-ls-cycling][data-cat="%c"] .ls-layer--%c') +
          '{transition:opacity ' + CFG.cross + 'ms var(--ls-ease) 0s}' +
        'section.ls-hero[data-ls-cycling][data-cat=""] .ls-layer{' +
          'transition:opacity ' + CFG.cross + 'ms var(--ls-ease) 0s}' +
      '}' +
      /* ---------- mobile ---------- */
      '@media screen and (max-width:749px){' +
        'section.ls-hero[data-ls-cycling] .ls-layer{' +
          'transition:opacity 0s linear ' + CFG.hold + 'ms}' +
        sel('section.ls-hero[data-ls-cycling][data-cat="%c"] .ls-layer--%c') +
          '{transition:opacity ' + CFG.cross + 'ms var(--mE) 0s}' +
        'section.ls-hero[data-ls-cycling][data-cat=""] .ls-layer{' +
          'transition:opacity ' + CFG.cross + 'ms var(--mE) 0s}' +
        'section.ls-hero[data-ls-cycling] .ls-mwedge{' +
          'transition:opacity ' + CFG.wcross + 'ms var(--mE) 0s}' +
        sel('section.ls-hero[data-ls-cycling][data-cat="%c"] .ls-mwedge--%c') +
          '{transition:opacity ' + CFG.wcross + 'ms var(--mE) 0s}' +
      '}';
    var st = document.createElement('style');
    st.id = 'ls-cycle-crossfade';
    st.appendChild(document.createTextNode(css));
    document.head.appendChild(st);
  }

  function boot() {
    var root = document.querySelector('section.ls-hero');
    if (!root) return;

    var layers = root.querySelectorAll('.ls-layer');
    if (layers.length !== SEQ.length) return;

    injectCSS();

    var mqWide = window.matchMedia('(min-width: ' + CFG.minw + 'px), (max-width: 749px)');
    var mqStill = window.matchMedia('(prefers-reduced-motion: reduce)');
    var mqMob = window.matchMedia('(max-width: 749px)');

    var idx = 0, revs = 0;
    var beatT = null, resumeT = null;
    var armed = false;    /* images verified present and decoded */
    var dead = false;     /* only ?lscycle=<n> revs can end it */
    var running = false;
    var held = false;     /* a pointer is over the hero, or focus is inside it */
    var onScreen = true, tabVisible = true;

    function clearTimers() {
      if (beatT) { clearTimeout(beatT); beatT = null; }
      if (resumeT) { clearTimeout(resumeT); resumeT = null; }
    }

    function eligible() {
      return armed && !dead && !held && onScreen && tabVisible &&
             mqWide.matches && !mqStill.matches;
    }

    /* Writing data-cat is the whole mechanism. Guarded so we can never write
       over a state the visitor's own pointer or keyboard is responsible for. */
    function write(v) {
      if (dead || held) return;
      root.setAttribute('data-cat', v);
    }

    function pause() {
      running = false;
      clearTimers();
      root.removeAttribute('data-ls-cycling');
    }

    function stopForGood() {
      dead = true;
      pause();
    }

    /* an interruption parks the loop; it never ends it */
    function parkAndResume() {
      pause();
      if (dead) return;
      idx = 0;
      write('');
      resumeT = setTimeout(function () { resumeT = null; start(0); }, CFG.resume);
    }

    function step() {
      beatT = null;
      if (!eligible()) { running = false; return; }

      if (idx < SEQ.length) {
        write(SEQ[idx]);
        idx += 1;
        beatT = setTimeout(step, CFG.beat);
        return;
      }

      /* the rest beat: idle, then round again */
      write('');
      idx = 0;
      revs += 1;
      if (revs >= CFG.revs) { stopForGood(); return; }
      beatT = setTimeout(step, CFG.rest);
    }

    function start(delay) {
      if (running || !eligible()) return;
      running = true;
      root.setAttribute('data-ls-cycling', '');
      clearTimers();
      beatT = setTimeout(step, typeof delay === 'number' ? delay : CFG.lead);
    }

    /* ---- readiness ------------------------------------------------------
       The section warms the four plates itself, after the LCP plate has
       finished AND the browser is idle. We wait for it rather than shortcut
       it: starting early would light a panel with no machine behind it. */
    function layersReady() {
      for (var i = 0; i < layers.length; i++) {
        var l = layers[i];
        if (!l.getAttribute('src') || !l.complete || !l.naturalWidth) return false;
      }
      return true;
    }

    var polls = 0;
    function whenReady(cb) {
      if (layersReady()) { cb(); return; }
      if (polls++ > 60) return;               /* ~24s ceiling, then stay quiet */
      setTimeout(function () { whenReady(cb); }, 400);
    }

    function arm() {
      /* decode before the first beat so the opening dissolve is not a decode
         stutter. decode() on a loaded image does not refetch. */
      var jobs = [];
      for (var i = 0; i < layers.length; i++) {
        if (typeof layers[i].decode === 'function') {
          try { jobs.push(layers[i].decode()['catch'](function () {})); } catch (e) {}
        }
      }
      var done = (jobs.length && window.Promise) ? Promise.all(jobs) : null;
      if (done) { done.then(function () { armed = true; start(CFG.lead); }); }
      else { armed = true; start(CFG.lead); }
    }

    /* ---- handing control back to the visitor ---------------------------- */

    /* Touch has no hover: a tap must park the loop, not hold it parked. */
    function hoverCapable(e) {
      return !e.pointerType || e.pointerType === 'mouse' || e.pointerType === 'pen';
    }

    root.addEventListener('pointerover', function (e) {
      if (!hoverCapable(e)) {
        var t = e.target;
        var onSeg = t && t.closest ? t.closest('.ls-seg,.ls-mseg') : null;
        if (onSeg) parkAndResume();     /* a tap: park, then start a fresh pass */
        return;
      }
      held = true;                       /* a real hover: hold until they leave */
      pause();
    }, true);

    root.addEventListener('pointerleave', function (e) {
      if (!hoverCapable(e)) return;
      held = false;
      if (dead) return;
      clearTimers();
      resumeT = setTimeout(function () { resumeT = null; start(0); }, CFG.resume);
    });

    /* A keyboard user tabbing into a segment must not have the dial change
       underneath them. */
    root.addEventListener('focusin', function (e) {
      var t = e.target;
      if (t && t.closest && t.closest('.ls-seg,.ls-mseg')) { held = true; pause(); }
    });

    root.addEventListener('focusout', function (e) {
      var to = e.relatedTarget;
      if (to && root.contains(to)) return;   /* still inside the hero */
      if (!held) return;
      held = false;
      if (dead) return;
      clearTimers();
      resumeT = setTimeout(function () { resumeT = null; start(0); }, CFG.resume);
    });

    document.addEventListener('visibilitychange', function () {
      tabVisible = !document.hidden;
      if (!tabVisible) pause(); else start(600);
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        onScreen = entries[0].isIntersecting;
        if (!onScreen) pause(); else start(600);
      }, { threshold: 0.35 }).observe(root);
    }

    function reassess() {
      if (!mqWide.matches || mqStill.matches) {
        pause();
        if (!held && root.getAttribute('data-cat')) root.setAttribute('data-cat', '');
      } else {
        start(600);
      }
    }
    [mqWide, mqStill, mqMob].forEach(function (mq) {
      if (mq.addEventListener) mq.addEventListener('change', reassess);
      else if (mq.addListener) mq.addListener(reassess);
    });

    whenReady(arm);
  }

  if (document.readyState === 'complete') boot();
  else window.addEventListener('load', boot);
})();

/* ---------------------------------------------------------------------------
   WhatsApp launcher: stays out of the hero (mobile only).

   The launcher is injected by the chat app as a fixed element in the
   bottom corner. On a phone the hero fills the viewport and the dial's
   outer panels reach that corner, so the launcher lands on top of a
   category link - measured at 320px, where it covered the STACKERS panel
   outright. This watches the hero and holds the launcher back while the
   hero is still meaningfully on screen, releasing it once the visitor has
   scrolled past.

   It touches nothing else: one class on <html>, read by a rule in
   liftstein-v2-skin.css. With no IntersectionObserver, or above 749px,
   nothing happens at all and the launcher behaves exactly as it did.
   --------------------------------------------------------------------------- */
(function () {
  if (!window.matchMedia || !window.matchMedia('(max-width: 749px)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  var root = document.documentElement;

  function arm() {
    var hero = document.querySelector('.ls-hero');
    if (!hero) return;

    root.classList.add('ls-wa-hold');

    var io = new IntersectionObserver(function (entries) {
      var e = entries[0];
      // held while more than a quarter of the hero is still in view
      root.classList.toggle('ls-wa-hold', e.isIntersecting && e.intersectionRatio > 0.25);
    }, { threshold: [0, 0.1, 0.25, 0.4] });

    io.observe(hero);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arm);
  } else {
    arm();
  }
})();
